<script>
  import CustomerPopover from "./CustomerPopover.svelte"
  import MapView from "./MapView.svelte"
  import StorageUnitDialog from "./StorageUnitDialog.svelte"
  import { fade } from "svelte/transition"
  import { getCookie, setCookie } from "utils"
  import { initMapSettings, initUpdateMapSettings, throttleScrollDelta } from "./utils"
  import { onDestroy, onMount, setContext } from "svelte"
  import { writable } from "svelte/store"

  export let getSiteMap = () => {}
  export let getUnitStatuses = () => {}
  export let isRestricted = true
  export let siteMapId = ""

  const defaultRect = {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
  }
  const minViewWidth = 50

  let containerElement = null
  let debugStore = { enabled: false }
  let defaultXCenter = getCookie(`${siteMapId}viewXCenter`)
  let defaultYCenter = getCookie(`${siteMapId}viewYCenter`)
  let defaultZoom = getCookie(`${siteMapId}zoom`)

  let selectedUnit = ""

  if (defaultXCenter) {
    defaultXCenter = Number(defaultXCenter)
  }

  if (defaultYCenter) {
    defaultYCenter = Number(defaultYCenter)
  }

  if (defaultZoom) {
    defaultZoom = Number(defaultZoom)
  }

  let dragState = null
  let loadError
  let mapBackgroundImageUrl
  let mapHeight
  let panState = null
  let selectedStorageUnitRowId
  let spacePressed = false
  let status = "loading"
  let storageUnitRows = []
  let storageUnitRowsById
  let storageUnitTypes
  let storageUnitTypesById
  let unassignedStorageUnits

  const customerPopoverStore = writable({
    containerRect: defaultRect,
    id: "",
    storageUnitRect: defaultRect,
  })

  const dialogStore = writable({ isOpen: false, storageUnitId: "" })
  const mapSettings = initMapSettings({ isEditing: false, viewWidth: defaultZoom || minViewWidth })
  const sitemapStatuses = writable({})
  const updateMapSettings = initUpdateMapSettings(mapSettings)

  let viewXCenter = defaultXCenter || $mapSettings.viewWidth / 2
  let viewYCenter = defaultYCenter || viewXCenter

  function buildStorageUnits(rows, $statuses) {
    const out = []

    rows.forEach((row) => {
      row.storageUnits.forEach((unit) => {
        out.push({ ...unit, ...$statuses[unit.id] })
      })
    })

    return out
  }

  function handleDomainPointerDown(e) {
    if (e.detail.entityType !== "storageUnitRow") {
      panState = {
        startElementX: e.detail.elementX,
        startElementY: e.detail.elementY,
        startViewXCenter: viewXCenter,
        startViewYCenter: viewYCenter,
        startXScale: e.detail.xScale.copy(),
        startYScale: e.detail.yScale.copy(),
      }
    } else {
      if (e.detail.entityType !== "storageUnitRow") {
        selectedStorageUnitRowId = null
      }
    }
  }

  function handleDomainPointerMove(e) {
    if (panState) {
      const offsetX =
        panState.startXScale.invert(e.detail.elementX) -
        panState.startXScale.invert(panState.startElementX)
      const offsetY =
        panState.startYScale.invert(e.detail.elementY) -
        panState.startYScale.invert(panState.startElementY)

      viewXCenter = panState.startViewXCenter - offsetX
      viewYCenter = panState.startViewYCenter - offsetY

      setCookie(`${siteMapId}viewYCenter`, viewYCenter, 365)
      setCookie(`${siteMapId}viewXCenter`, viewXCenter, 365)
    }
  }

  async function handleDomainPointerUp(e) {
    panState = null

    if (dragState) {
      if (dragState.startX != dragState.currentX && dragState.startY != dragState.currentY) {
        try {
          const storageUnitRow = storageUnitRowsById.get(dragState.storageUnitRowId)

          const response = await updateSiteMapRow(
            siteMapId,
            dragState.storageUnitRowId,
            storageUnitRow.position
          )
          if (!response.ok) {
            throw new Error("Failed to save storage unit row position")
          }
        } catch (error) {
          status = "saveFailed"
          loadError = error.message
        }
      }

      dragState = null
    } else {
      selectedStorageUnitRowId = null
    }
  }

  function handleKeyDown(event) {
    if (event.key === " ") {
      event.preventDefault()
      spacePressed = true
    } else if (event.key === "=" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      zoomIn()
    } else if (event.key === "-" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      zoomOut()
    } else if (event.key === "0" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      zoomReset()
    } else if (event.key === "d") {
      debugStore.enabled = !debugStore.enabled
    }
  }

  function handleKeyUp(event) {
    if (event.key === " ") {
      event.preventDefault()
      spacePressed = false
    }
  }

  function handleWheel(event) {
    event.preventDefault()

    const multiplier = event.ctrlKey ? 30 : 8.5
    const maxViewWidth = $mapSettings.mapWidth * 5

    let scale = $mapSettings.viewWidth + throttleScrollDelta(event) * multiplier
    scale = Math.min(maxViewWidth, Math.max(minViewWidth, scale))
    updateMapSettings({ viewWidth: scale })

    setCookie(`${siteMapId}zoom`, $mapSettings.viewWidth, 365)
  }

  async function load({ shouldCenterMap = false, shouldFetchStatuses = false }) {
    try {
      if (shouldFetchStatuses) {
        const unitStatuses = await getUnitStatuses(siteMapId)

        if (unitStatuses.ok) {
          sitemapStatuses.set(unitStatuses.units)
        }
      }

      const siteMap = await getSiteMap(siteMapId)
      storageUnitTypes = siteMap.storageUnitTypes
      for (const storageUnitType of storageUnitTypes) {
        storageUnitType.shape.width = Math.max(...storageUnitType.shape.map((p) => p[0]))
        storageUnitType.shape.height = Math.max(...storageUnitType.shape.map((p) => p[1]))
      }
      storageUnitTypesById = new Map(siteMap.storageUnitTypes.map((sut) => [sut.id, sut]))

      storageUnitRows = siteMap.storageUnitRows
      storageUnitRowsById = new Map(siteMap.storageUnitRows.map((sur) => [sur.id, sur]))

      unassignedStorageUnits = siteMap.unassignedStorageUnits
      updateMapSettings({ mapWidth: siteMap.width })
      mapHeight = siteMap.height
      mapBackgroundImageUrl = siteMap.backgroundImageUrl

      if (shouldCenterMap) {
        if ($mapSettings.viewWidth === minViewWidth) {
          updateMapSettings({ viewWidth: $mapSettings.mapWidth })
        }
        if (!defaultXCenter) {
          viewXCenter = $mapSettings.viewWidth / 2
        }
        if (!defaultYCenter) {
          viewYCenter = viewXCenter
        }
      }

      setTimeout(() => {
        status = "ready"

        requestAnimationFrame(updateCustomerPopoverContainerRect)
      }, 500)
    } catch (error) {
      loadError = error.message
      setTimeout(() => {
        status = "loadFailed"
      }, 500)
    }
  }

  function updateCustomerPopoverContainerRect() {
    if (!containerElement) return

    customerPopoverStore.update((current) => ({
      ...current,
      containerRect: containerElement.getBoundingClientRect(),
    }))
  }

  function updateDebugStore(options) {
    debugStore = { ...debugStore, ...options }
  }

  function zoomIn() {
    zoomMap(0.7)
  }

  function zoomMap(multiplier) {
    const maxViewWidth = $mapSettings.mapWidth * 5

    let newWidth = $mapSettings.viewWidth * multiplier
    newWidth = Math.min(maxViewWidth, Math.max(minViewWidth, newWidth))

    updateMapSettings({ viewWidth: newWidth })
    setCookie(`${siteMapId}zoom`, $mapSettings.viewWidth, 365)
  }

  function zoomOut() {
    zoomMap(1.35)
  }

  function zoomReset() {
    updateMapSettings({ viewWidth: $mapSettings.mapWidth * 3 })
    zoomMap(1)

    // Force StorageUnit unit text to re-position
    requestAnimationFrame(() => {
      zoomIn()
      zoomOut()
    })
  }

  function recenterMap() {
    // Reset zoom to default
    updateMapSettings({ viewWidth: $mapSettings.mapWidth * 3 })
    zoomMap(1)

    // Re-center the map position to the actual center of the map
    viewXCenter = $mapSettings.mapWidth / 2
    viewYCenter = mapHeight / 2

    // Update cookies to persist the centered position
    setCookie(`${siteMapId}viewXCenter`, viewXCenter, 365)
    setCookie(`${siteMapId}viewYCenter`, viewYCenter, 365)
    setCookie(`${siteMapId}zoom`, $mapSettings.viewWidth, 365)

    // Force StorageUnit unit text to re-position
    requestAnimationFrame(() => {
      zoomIn()
      zoomOut()
    })
  }

  setContext("customerPopoverStore", customerPopoverStore)
  setContext("dialogStore", dialogStore)
  setContext("mapSettings", mapSettings)
  setContext("siteMapId", siteMapId)
  setContext("siteMapReload", load)
  setContext("sitemapStatuses", sitemapStatuses)
  setContext("updateDebugStore", updateDebugStore)

  onDestroy(() => {
    window.removeEventListener("resize", updateCustomerPopoverContainerRect)
    window.removeEventListener("scroll", updateCustomerPopoverContainerRect)
  })

  onMount(() => {
    load({ shouldCenterMap: true, shouldFetchStatuses: true })

    window.addEventListener("resize", updateCustomerPopoverContainerRect)
    window.addEventListener("scroll", updateCustomerPopoverContainerRect)
  })

  $: allStorageUnits = buildStorageUnits(storageUnitRows, $sitemapStatuses)

  $: updateDebugStore({
    viewXCenter: viewXCenter.toFixed(4),
    viewYCenter: viewYCenter.toFixed(4),
    viewWidth: $mapSettings.viewWidth.toFixed(4),
  })
</script>

<svelte:window
  on:keydown={handleKeyDown}
  on:keyup={handleKeyUp}
  onresize={updateCustomerPopoverContainerRect}
/>
<svelte:body on:mouseup={handleDomainPointerUp} />

{#if status == "ready"}
  <div
    bind:this={containerElement}
    class="bg-gray-100 h-[100vw] md:h-[70vh] relative w-full flex drop-shadow z-10 select-none"
    transition:fade
  >
    <div class="flex-1 overflow-hidden touch-none" on:wheel={handleWheel}>
      {#if debugStore.enabled}
        <div
          transition:fade
          class="rounded-lg p-3 bg-black/50 text-white absolute z-10 top-4 right-4 max-w-[300px]"
        >
          {#each Object.entries(debugStore) as [key, value]}
            <div>{key}: {value}</div>
          {/each}
        </div>
      {/if}

      <MapView
        mapWidth={$mapSettings.mapWidth}
        on:domainpointerdown={handleDomainPointerDown}
        on:domainpointermove={handleDomainPointerMove}
        viewWidth={$mapSettings.viewWidth}
        {mapBackgroundImageUrl}
        {mapHeight}
        {selectedStorageUnitRowId}
        {spacePressed}
        {storageUnitRows}
        {storageUnitTypesById}
        {viewXCenter}
        {viewYCenter}
      />

      <div class="absolute bottom-4 bg-black/50 md:right-4 right-8 rounded-xl text-white">
        <div class="bg-black/50 shadow-lg rounded-lg text-white overflow-clip">
          <div class="divide-y rounded-xl">
            <button
              aria-label="Zoom in"
              on:click={(e) => {
                e.preventDefault()
                zoomIn()
              }}
              class="hover:bg-black/20 cursor-pointer p-4 w-10 h-10 flex items-center justify-center text-2xl"
              title="Zoom in (Ctrl++)"
            >
              +
            </button>
            <button
              aria-label="Reset zoom"
              on:click={(e) => {
                e.preventDefault()
                zoomReset()
              }}
              class="hover:bg-black/20 cursor-pointer w-10 h-10 flex items-center justify-center text-2xl"
              title="Reset zoom (Ctrl+0)"
            >
              <svg class="w-5 h-5 text-white-500">
                <svg
                  id="arrow-path"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z"
                    clip-rule="evenodd"
                  />
                </svg>
              </svg>
            </button>
            <button
              aria-label="Zoom out"
              on:click={(e) => {
                e.preventDefault()
                zoomOut()
              }}
              class="hover:bg-black/20 cursor-pointer p-4 w-10 h-10 flex items-center justify-center text-2xl"
              title="Zoom out (Ctrl+-)"
            >
              -
            </button>
            <button
              aria-label="Re-center map"
              on:click={(e) => {
                e.preventDefault()
                recenterMap()
              }}
              class="hover:bg-black/20 cursor-pointer w-10 h-10 flex items-center justify-center text-2xl"
              title="Re-center map"
            >
              <svg
                class="w-6 h-6 text-white-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="1" fill="currentColor" />
                <line x1="12" y1="2" x2="12" y2="6" />
                <line x1="12" y1="18" x2="12" y2="22" />
                <line x1="2" y1="12" x2="6" y2="12" />
                <line x1="18" y1="12" x2="22" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    {#if !isRestricted}
      <StorageUnitDialog storageUnits={allStorageUnits} />
    {/if}

    {#if !isRestricted}
      {#each allStorageUnits as storageUnit}
        {#if storageUnit.id == $customerPopoverStore.id}
          <CustomerPopover {storageUnit} />
        {/if}
      {/each}
    {/if}
  </div>
{:else if status != "loading"}
  <div transition:fade class="flex h-full items-center justify-center">
    <div>
      <div class="flex items-center justify-center">
        <svg class="w-8 h-8 text-red-500 mr-2">
          <svg
            id="exclamation-circle"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
              clip-rule="evenodd"
            />
          </svg>
        </svg>
        <p class="text-red-500 text-3xl text-center">Error</p>
      </div>

      <p class="text-red-500 text-xl text-center">
        {loadError}
      </p>
    </div>
  </div>
{/if}

{#if status == "loading"}
  <div transition:fade class="flex h-full items-center text-slate-700 justify-center">
    <svg
      class="animate-spin -ml-1 mr-3 h-8 w-8"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <p class="text-slate-700 text-3xl">Site map loading...</p>
  </div>
{/if}
